/* eslint-disable jsx-a11y/anchor-has-content */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import styled from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import tinycolor from 'tinycolor2';
import { DynamicThemeProvider, getThemeProp, media } from '@amtrav/themes';
import { ClickableGraphic, Card, AutocompleteInput, FullPageLoader, Tooltip } from '@amtrav/view-components';
import {
  IconArrowUpLong,
  IconChainLink,
  IconUSAFlag,
  IconEarth,
  IconPlaneLine,
  IconCarLine,
  IconHotelLine,
  IconInfo,
  IconChatSquare,
} from '@amtrav/icons';
import PageContent from '../../js-local/core/components/layout/PageContent';
import { PageHeaderRegion } from '../../js-local/core/components/layout/PageHeader';
import Image from '../../js-global/core/components/elements/image/Image';
import Disclaimer from './Disclaimer';
import { fetchRegionalAlerts } from './webservices';

const OverridePageContent = styled.div`
  & ${PageHeaderRegion} {
    margin-top: 0;
  }
`;

const LogoWrapper = styled.a`
  width: 140px;

  ${media.Small`
    width: 225px;
  `}
`;

const TypeHeading = styled(Flex)`
  font-size: 26px;
  font-weight: 600;
  align-items: center;
`;

const ContentHeading = styled(Flex)`
  font-size: 26px;
  margin: 0 0 16px;
  align-items: center;

  & button {
    display: none;
  }

  & :hover {
    & button {
      display: inline;
    }
  }
`;

const MainContentWrapper = styled(Box)`
  background-color: #f1f1f1;

  & a {
    color: ${getThemeProp('global.link--color')};
    word-break: break-word;
  }
`;

const ContentBox = styled(Box)`
  & h3 {
    font-size: 22px;
    margin: 16px 0 8px 0;
  }

  & h4 {
    margin: 16px 0 8px 0;
    font-size: 20px;
    font-weight: bold;
  }

  & ul {
    margin: 0 0 8px 0;
  }

  & li {
    margin: 8px 0;
    line-height: 1.3;

    a {
      word-break: break-word;
    }
  }

  & ol {
    margin: 8px 0;
  }

  p {
    margin: 0 0 8px 0;
  }

  table {
    width: 320px !important;
  }
`;

const FlagImage = styled.div`
  width: 40px;
  height: 40px;
  margin-right: 10px;

  img {
    width: 40px;
    height: 40px;
  }
`;

const ContentLookupStyled = styled.a`
  font-size: 16px;
  text-decoration: none;
`;

const Link = styled(Flex)`
  font-size: 16px;
  align-items: center;
  color: ${getThemeProp('global.link--color')};

  a {
    text-decoration: none;
  }
`;

const IncomingMarkup = styled(Box)`
  line-height: 1.3;
`;

const LargeIcon = styled(Box)`
  height: 30px;
  width: 30px;
  font-size: 30px;
  margin-right: 8px;
`;

const SmallIcon = styled(Box)`
  font-size: 22px;
  height: 22px;
  margin-right: 8px;
`;

const TopContent = styled(Flex)`
  margin: auto;
`;

const TopContentBkgd = styled(Flex)`
  width: 100%;
  background-color: ${(props) => tinycolor(getThemeProp('global.light--color')(props)).darken(6).toString()};
  box-shadow: ${getThemeProp('card.box-shadow')};
`;

const ResourcesContent = styled(Box)`
  margin: auto;
`;

const DisclaimerCont = styled(Flex)`
  height: 16px;
  align-items: center;
  color: ${getThemeProp('global.warning--color')};
  cursor: pointer;
  margin: 8px 0;
`;

const InfoWrapper = styled.div`
  font-size: 16px;
  margin-right: 5px;
`;

const SectionLinks = styled(Flex)`
  height: unset;
  flex-wrap: wrap;
  background-color: #e2f1ff;
  padding-left: 12px;
  padding-right: 12px;
  line-height: 1.3;

  ${media.Small`
   height: 130px;
   justify-content: center;
   line-height: unset;
  `}
`;

const CustomHeader = () => {
  return (
    <Flex
      alignItems={['flex-start', 'flex-start', 'center']}
      flexDirection={['column', 'column', 'row']}
      mb={['-8px', '-8px', 0]}>
      <LogoWrapper href="/">a2b by AmTrav</LogoWrapper>
      <Box ml={[0, 0, 3]} pt={[0, 0, '10px']}>
        COVID Resource Center
      </Box>
    </Flex>
  );
};

const iconCheck = (icn) => {
  switch (icn) {
    case 'usaFlag':
      return <IconUSAFlag />;
    case 'globe':
      return <IconEarth />;
    case 'plane':
      return <IconPlaneLine />;
    case 'hotel':
      return <IconHotelLine />;
    case 'car':
      return <IconCarLine />;
    default:
      return null;
  }
};
const idSlug = (str) => str.toLowerCase().replace(/ /g, '-');

// Check for null because if user hits esc it will send null to this func.
const itemToString = (item) => (item ? item.name : '');

// The hash is removed from the URL (possibly from the legacy JSR handling). This captures it
// before it is removed and saves it to be replaced/used after the API results come back.
const initialHash = window.location.hash.replace('#', '');
const setHash = (hash) => {
  window.location.hash = hash;
};

const ResourceCenterApp = ({ themeName }) => {
  const [apiResults, setApiResults] = useState({});
  const [activeInfoBox, setActiveInfoBox] = useState(null);

  const activeInfoBoxRef = useRef(null);

  const scrollToTop = () => {
    setActiveInfoBox(null);
    setHash('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const mapContent = (resource, title, iconType) => {
    const wrappedContent = resource.map((r) => {
      const contentId = r.code;
      const copyHashedUrl = () => {
        setHash(contentId);
      };
      return (
        <ContentBox key={contentId} id={contentId} ref={contentId === activeInfoBox ? activeInfoBoxRef : null}>
          <Card p="20px 24px 16px 24px" mb={3}>
            <Flex flexDirection="column">
              <ContentHeading>
                {r.code.length < 3 ? (
                  <FlagImage>
                    <Image
                      alt="Country Flag"
                      src={`https://cdn.amtrav.com/flags-country/${r.code}.png`}
                      renderFallback={() => (
                        <Box p="6px">
                          <IconEarth />
                        </Box>
                      )}
                    />
                  </FlagImage>
                ) : null}
                {r.name}{' '}
                <Box ml={2} mt="-2px">
                  <ClickableGraphic
                    kind="primary"
                    text="link"
                    size="small"
                    iconPrefix={<IconChainLink />}
                    onClick={copyHashedUrl}
                  />
                </Box>
              </ContentHeading>
              <IncomingMarkup pb={3} dangerouslySetInnerHTML={{ __html: r.content }} />
              <Box alignSelf="flex-end">
                <ClickableGraphic
                  text="Back to top"
                  size="small"
                  iconSuffix={<IconArrowUpLong />}
                  iconSize="16px"
                  onClick={scrollToTop}
                />
              </Box>
            </Flex>
          </Card>
        </ContentBox>
      );
    });

    return (
      <ResourcesContent width={['100%', '1024px']} id={idSlug(title)} key={idSlug(title)}>
        <TypeHeading m={['30px 16px 16px', '30px 0 16px 0']}>
          <LargeIcon>{iconCheck(iconType)}</LargeIcon>
          {title}
        </TypeHeading>
        <Box>{wrappedContent}</Box>
      </ResourcesContent>
    );
  };

  useEffect(() => {
    fetchRegionalAlerts().then((results) => {
      setApiResults(results);
      setActiveInfoBox(initialHash);
      // For some reason the hash is removed from the URL. This places it back
      setHash(initialHash);
    });
  }, []);

  useEffect(() => {
    if (activeInfoBoxRef.current) {
      activeInfoBoxRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeInfoBox]);

  const searchItems = Object.keys(apiResults).reduce((acc, key) => {
    const keyItems = apiResults[key].map((item) => ({
      id: item.code,
      name: item.name,
    }));
    return acc.concat(keyItems);
  }, []);

  const onSearchChange = (newSelection) => {
    if (newSelection) {
      setActiveInfoBox(newSelection.id);
      setHash(newSelection.id);
    }
  };

  const { airlines, cars, countries, hotels, states } = apiResults;

  if (Object.keys(apiResults).length === 0) {
    // return 'Loading...';
    return (
      <DynamicThemeProvider themeName={themeName}>
        <FullPageLoader />
      </DynamicThemeProvider>
    );
  }

  const contentDataLookup = {
    states: {
      content: states,
      title: 'State-by-State Policies',
      iconType: 'usaFlag',
    },
    countries: {
      content: countries,
      title: 'Country-Level Restrictions & Guidelines',
      iconType: 'globe',
    },
    airlines: {
      content: airlines,
      title: 'Airline Safety & Policies',
      iconType: 'plane',
    },
    hotels: {
      content: hotels,
      title: 'Hotel Brand Safety & Policies',
      iconType: 'hotel',
    },
    cars: {
      content: cars,
      title: 'Ground Transportation Provider Safety & Policies',
      iconType: 'car',
    },
  };

  const contentBlocks = Object.keys(contentDataLookup).map((key) => {
    return contentDataLookup[key]
      ? mapContent(
          contentDataLookup[key].content,
          contentDataLookup[key].title,
          contentDataLookup[key].iconType,
          activeInfoBox,
        )
      : null;
  });

  const sectionLinks = Object.keys(contentDataLookup).map((key) => {
    const slug = idSlug(contentDataLookup[key].title);
    const { iconType } = contentDataLookup[key];
    return contentDataLookup[key] ? (
      <Box my={2} key={slug} width={['100%', '506px']}>
        <ContentLookupStyled href={`#${slug}`}>
          <Flex alignItems="center">
            <SmallIcon>{iconCheck(iconType)}</SmallIcon>
            {contentDataLookup[key].title}
          </Flex>
        </ContentLookupStyled>
      </Box>
    ) : null;
  });

  const mainContent = (
    <MainContentWrapper width={1}>
      <TopContentBkgd>
        <TopContent px={[3, 0]} py={3} flexDirection={['column', 'column']} width={['100%', '1024px']}>
          <SectionLinks pb={[1, 0]} mb={[0, 3]} width={1} flexDirection="column">
            {sectionLinks}
            <DisclaimerCont width={['100%', '506px']}>
              <InfoWrapper>
                <IconInfo />
              </InfoWrapper>
              <Tooltip content={<Disclaimer />} kind="light" interactive placement="top">
                Important information about this data
              </Tooltip>
            </DisclaimerCont>
          </SectionLinks>
          <Flex width={1} flexDirection={['column', 'row']} justifyContent="space-between">
            <Link my={[3, 0]} justifyContent={['flex-start', 'flex-end']}>
              <SmallIcon>
                <IconChatSquare />
              </SmallIcon>
              <a href="/chat">Contact Us </a>
            </Link>
            <Box width={['100%', '506px']}>
              <AutocompleteInput
                items={searchItems}
                itemToString={itemToString}
                onChange={onSearchChange}
                labelKey="name"
                placeholder="US State, Country, Airline, Hotel or Car"
              />
            </Box>
          </Flex>
          <Link>
            <a href="https://www.tripsguard.com">
              Powered by <b>tripsguard.com</b>
            </a>
          </Link>
        </TopContent>
      </TopContentBkgd>
      <div id="top-of-page" />
      {contentBlocks}
    </MainContentWrapper>
  );

  return (
    <DynamicThemeProvider themeName={themeName}>
      <OverridePageContent>
        <PageContent fullWidth pageHeader={<CustomHeader />} mainContent={mainContent} />
      </OverridePageContent>
    </DynamicThemeProvider>
  );
};

ResourceCenterApp.propTypes = {
  themeName: PropTypes.string,
};

export default process.env.USE_HMR ? hot(ResourceCenterApp) : ResourceCenterApp;
